import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { ParkingService } from '../../../services/parking.service'
import { ActivatedRoute, Router } from '@angular/router'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import * as moment from 'moment'
import {
    AccordionState,
    clientsPath,
    ContractStatus,
    Image,
    Occupant,
    occupantsPath,
    Parking,
    parkingsPath,
    ParkingType,
    PayGateClient,
    Plan,
    Subscription,
    Term,
    TrackingEntryType,
    SubscriptionType,
    UserActionLog,
} from '@parkupp/core'
import { SubscriptionService } from '../../../services/subscription.service'
import { AuthenticationService } from '../../../services/authentication.service'
import { addDoc, collection, collectionData, doc, docData, query, serverTimestamp, Timestamp } from '@angular/fire/firestore'
import { ClientService } from '../../../services/client.service'
import { SortImagesByDatePipe } from '../../core/pipes/sort-images-by-date.pipe'
import { PaymentService } from '../../../services/payment.service'
import { ToastrService } from 'ngx-toastr'
import { AmenityDirective } from '../../core/directives/amenity.directive'
import { Meta, Title } from '@angular/platform-browser'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import * as firestore from 'firebase/firestore'
import { SortImagesByPrimaryImagePipe } from '../../core/pipes/sort-images-by-primary-image.pipe'
import { OmnisendService } from '../../../services/omnisend.service'
import { GaActionEnum, GoogleAnalyticsService } from 'ngx-google-analytics'
import { GlobalService } from '../../../services/global.service'
import { TrackingService } from '../../../services/tracking.service'
import { BookingService } from '../../../services/booking.service'
import { slideAnimation } from '../../animations/slide.animation'
import { environment } from 'src/environments/environment'
import { firstValueFrom, Observable } from 'rxjs'
import { HttpClient } from '@angular/common/http'

@Component({
    selector: 'app-parking-page-classic',
    templateUrl: './parking-page-classic.component.html',
    styleUrls: ['./parking-page-classic.component.scss'],
    animations: [slideAnimation], // Make sure this matches
})
export class ParkingPageClassicComponent implements OnInit {
    SubscriptionType = SubscriptionType
    allowedPaymentType = SubscriptionType.PAYGATE
    parkingKey: string
    parking: Parking

    formData: FormGroup
    formLoading: boolean = false
    formError: string
    selectedPlan: Plan
    openPopup: boolean = false
    subscriptionReference: string = ''
    subscriptions: Subscription[]
    images: Image[] = []
    highestIndex = 0
    currentImageIndex = 1
    typeSpecificAmenities: any = {}

    // Occupants
    @ViewChild('popupOccupant') popupOccupant: TemplateRef<any>
    modalRefOccupant?: BsModalRef | null
    formDataOccupant: FormGroup
    formLoadingOccupant: boolean = false
    formErrorOccupant: string

    occupants: Occupant[]
    occupantLimit: number = 1

    showNumberOfBaysLeft: boolean = false
    showContractDuration: boolean = true
    showStartAndEndDate: boolean = true
    showNumberOfBaysSelected: boolean = true
    showSelectOccupants: boolean = false
    showPlanSelector = false

    // Flex
    showFlexDateSelector: boolean = false
    startOfThisMonth: moment.Moment
    middleOfThisMonth: moment.Moment
    endOfThisMonth: moment.Moment
    startOfNextMonth: moment.Moment
    endOfNextMonth: moment.Moment

    totalDailySavings: number
    flexDateSelectorOptions: any[] = []

    bookingMinDate: string
    bookingMaxDate: string
    modalRef: BsModalRef<unknown>
    accordionAboutState = AccordionState.COLLAPSED // This controls the accordion state
    accordionContactState = AccordionState.COLLAPSED // This controls the accordion state
    accordionHowItWorksState = AccordionState.COLLAPSED // This controls the accordion state
    accordionBookYourBayState = AccordionState.EXPANDED // This controls the accordion state
    aboutThisParking = AccordionState.COLLAPSED // This controls the accordion state
    showMobileMap = false
    loggedInUser: any
    user$: any
    isAuthenticated = false

    constructor(
        private toastrService: ToastrService,
        private parkingService: ParkingService,
        private clientService: ClientService,
        private router: Router,
        private subscriptionService: SubscriptionService,
        private authenticationService: AuthenticationService,
        private activatedRoute: ActivatedRoute,
        private titleService: Title,
        private metaTagService: Meta,
        private modalService: BsModalService,
        private omnisendService: OmnisendService,
        private googleAnalyticsService: GoogleAnalyticsService,
        public globalService: GlobalService,
        private trackingService: TrackingService,
        private http: HttpClient
    ) {
        const urlParams = new URLSearchParams(window.location.search)
        if (urlParams.get('allowPaymentType') && urlParams.get('allowPaymentType') === SubscriptionType.PAYFAST) {
            this.allowedPaymentType = SubscriptionType.PAYFAST
        }
        if (urlParams.get('allowPaymentType') && urlParams.get('allowPaymentType') === SubscriptionType.EFT) {
            this.allowedPaymentType = SubscriptionType.EFT
        }

        this.authenticationService.authenticatedUser$.subscribe((user) => {
            this.loggedInUser = user
            if (user && !user.isAnonymous) {
                this.isAuthenticated = true
            }
        })

        this.parkingKey = this.activatedRoute.snapshot.params['parkingKey']
        this.selectedPlan = new Plan()

        docData(doc(this.parkingService.firestore, parkingsPath(), this.parkingKey)).subscribe((documentData: any) => {
            this.parking = new Parking(documentData)
            this.parking.$key = this.parkingKey

            // Meta Data
            this.titleService.setTitle(`Parking Available: ${this.parking.title} in ${this.parking.parkingLocation.shortAddress}`)
            this.metaTagService.updateTag({ name: 'description', content: this.parking.metaDescription! })

            this.images = []
            if (this.parking.images) {
                for (const key in this.parking.images) {
                    this.images.push(this.parking.images[key])
                }
                this.images = SortImagesByDatePipe.sortImages(this.images)
                this.images = SortImagesByPrimaryImagePipe.sortImages(this.images, this.parking.imagePrimary ? this.parking.imagePrimary : '')
            }

            this.typeSpecificAmenities = AmenityDirective.amenities(this.parking.parkingType)

            // Set the form data
            this.formData = new FormGroup({
                planTerm: new FormControl(null, [Validators.required]),
                baysBooked: new FormControl(1, [Validators.required, Validators.min(1)]),
                startDate: new FormControl(moment().format('yyyy-MM-DD'), [Validators.required]),
                endDate: new FormControl(moment().add(1, 'M').subtract(1, 'd').format('yyyy-MM-DD'), [Validators.required]),
                contractLength: new FormControl(1, [Validators.required, Validators.min(1)]),
                occupants: new FormControl(''),
                flexDateSelector: new FormControl(null),
                bayNumber: new FormControl(''),
            })

            // Parking type options
            if (this.parking.parkingType == ParkingType.PARKING_EVENTS) {
                this.updateSelectedPlan(this.parking?.plans?.find((i) => i.term === Term.ONCEOFF) as Plan)
                this.formData.controls['startDate'].setValue(this.parking.startDate)
                this.showNumberOfBaysLeft = true
                this.showContractDuration = false
                this.showNumberOfBaysSelected = false
                this.showSelectOccupants = true
                this.showStartAndEndDate = false
            }
            if (this.parking.parkingType == ParkingType.FLEX) {
                this.updateSelectedPlan(this.parking?.plans?.find((i) => i.term === Term.FLEX_MONTH) as Plan)

                // Now we can safely modify the validators
                this.formData.get('occupants')?.setValidators(null)
                this.formData.get('occupants')?.updateValueAndValidity()

                this.showContractDuration = false
                this.showFlexDateSelector = true
                this.showStartAndEndDate = false

                this.startOfThisMonth = moment().startOf('month')
                this.middleOfThisMonth = moment().startOf('month').add(14, 'days')
                this.endOfThisMonth = moment().endOf('month')
                this.startOfNextMonth = moment().add(1, 'months').startOf('month')
                this.endOfNextMonth = moment().add(1, 'months').endOf('month')
                this.flexDateSelectorOptions = []

                if (parseInt(moment().format('DD')) < 15) {
                    if (this.parking?.plans?.find((i) => i.term === Term.FLEX_HALF_MONTH)) {
                        this.flexDateSelectorOptions.push({ key: 'halfMonth', value: this.middleOfThisMonth.format('D MMM YYYY') + ' - ' + this.endOfThisMonth.format('D MMM YYYY') + ' (half month)' })
                    }
                    this.flexDateSelectorOptions.push({ key: 'thisMonth', value: this.startOfThisMonth.format('D MMM YYYY') + ' - ' + this.endOfThisMonth.format('D MMM YYYY') })
                } else {
                    if (this.parking?.plans?.find((i) => i.term === Term.FLEX_HALF_MONTH)) {
                        this.flexDateSelectorOptions.push({ key: 'halfMonth', value: this.middleOfThisMonth.format('D MMM YYYY') + ' - ' + this.endOfThisMonth.format('D MMM YYYY') + ' (half month)' })
                    } else {
                        this.flexDateSelectorOptions.push({ key: 'thisMonth', value: this.startOfThisMonth.format('D MMM YYYY') + ' - ' + this.endOfThisMonth.format('D MMM YYYY') })
                    }
                }

                this.flexDateSelectorOptions.push({ key: 'nextMonth', value: this.startOfNextMonth.format('D MMM YYYY') + ' - ' + this.endOfNextMonth.format('D MMM YYYY') })
                this.flexDateSelectorChanged('nextMonth')
            } else {
                this.showSelectOccupants = true
                this.updateSelectedPlan(this.parking?.plans?.find((i) => i.term === Term.MONTHLY) as Plan)
            }

            if (this.parking.parkingType == ParkingType.BOOKING) {
                this.formData = new FormGroup({
                    startDate: new FormControl(moment().format('yyyy-MM-DD'), [Validators.required]),
                    bayNumber: new FormControl('', Validators.required),
                    occupants: new FormControl('', Validators.required),
                })

                const currentDate = new Date()
                this.bookingMinDate = moment(currentDate).format('YYYY-MM-DD')
                this.bookingMaxDate = moment(currentDate).add(7, 'days').format('YYYY-MM-DD')
            }

            this.updateEndDate(this.selectedPlan.term, this.formData.value.startDate, 1)

            if (this.formData.controls['planTerm']) {
                this.formData.controls['planTerm'].valueChanges.subscribe((value) => this.formChanges({ planTerm: value }))
            }
            if (this.formData.controls['baysBooked']) {
                this.formData.controls['baysBooked'].valueChanges.subscribe((value) => this.formChanges({ baysBooked: value }))
            }
            if (this.formData.controls['contractLength']) {
                this.formData.controls['contractLength'].valueChanges.subscribe((value) => this.formChanges({ contractLength: value }))
            }
            if (this.formData.controls['flexDateSelector']) {
                this.formData.controls['flexDateSelector'].valueChanges.subscribe((value) => this.flexDateSelectorChanged(value))
            }
            this.formData.controls['startDate'].valueChanges.subscribe((value) => this.formChanges({ startDate: value }))

            this.totalDailySavings = this.parking.dailySavings * 30.5
        })

        // Occupant
        this.formDataOccupant = new FormGroup({
            numberPlate: new FormControl('', Validators.required),
            model: new FormControl('', Validators.required),
            colour: new FormControl('', Validators.required),
            fullName: new FormControl(''),
            description: new FormControl(''),
        })

        // Whitelabel
        if (this.globalService.subdomain == 'redefine') {
            document.documentElement.style.setProperty('--background-color', 'white')
        } else {
            document.documentElement.style.setProperty('--background-color', '#F7F7F7')
        }
    }

    // readonly ParkingType = ParkingType

    get termFee() {
        return this.selectedPlan.feeInCents * this.formData.value.baysBooked
    }

    toggleAccordion(accordion: string) {
        if (accordion == 'accordionAboutState') {
            this.accordionAboutState = this.accordionAboutState === AccordionState.COLLAPSED ? AccordionState.EXPANDED : AccordionState.COLLAPSED
        }
        if (accordion == 'accordionHowItWorksState') {
            this.accordionHowItWorksState = this.accordionHowItWorksState === AccordionState.COLLAPSED ? AccordionState.EXPANDED : AccordionState.COLLAPSED
        }
        if (accordion == 'accordionBookYourBayState') {
            this.accordionBookYourBayState = this.accordionBookYourBayState === AccordionState.COLLAPSED ? AccordionState.EXPANDED : AccordionState.COLLAPSED
        }
        if (accordion == 'accordionContactState') {
            this.accordionContactState = this.accordionContactState === AccordionState.COLLAPSED ? AccordionState.EXPANDED : AccordionState.COLLAPSED
        }
        if (accordion == 'aboutThisParking') {
            this.aboutThisParking = this.aboutThisParking === AccordionState.COLLAPSED ? AccordionState.EXPANDED : AccordionState.COLLAPSED
        }
    }

    ngOnInit(): void {
        this.clientService.activeClient$.subscribe((activeClient) => {
            if (activeClient && activeClient.$key) {
                // TODO: Mark - this is firing twice
                const parkingRef = doc(this.parkingService.firestore, parkingsPath(), this.parkingKey)
                const clientRef = doc(this.parkingService.firestore, clientsPath(), activeClient.$key)

                // const ref = query(
                //     collection(this.clientService.firestore, subscriptionsPath()),
                //     where('paymentInitiated', '==', true),
                //     where('clientRef', '==', clientRef),
                //     where('parkingRef', '==', parkingRef),
                //     orderBy('createdAt', 'desc')
                // )
                // collectionData(ref, { idField: 'documentId' }).subscribe((documents: any[]) => {
                //     this.subscriptions = []
                //     documents.forEach((documentData) => {
                //         const subscription = new Subscription(documentData)
                //         subscription.$key = documentData.documentId
                //
                //         docData(documentData.parkingRef).subscribe((data) => {
                //             subscription.$parking = data as Parking
                //         })
                //         docData(documentData.clientRef).subscribe((data) => {
                //             subscription.$client = data as Client
                //         })
                //         this.subscriptions.push(subscription)
                //     })
                // })

                // const refOccupants = query(collection(this.clientService.firestore, occupantsPath(this.clientService.activeClient?.$key as string)))
                // collectionData(refOccupants, { idField: 'documentId' }).subscribe((documents: any[]) => {
                //     this.occupants = []
                //     documents.forEach((documentData) => {
                //         const occupant = new Occupant(documentData)
                //         occupant.$key = documentData.documentId

                //         if (!occupant.deletedAt) {
                //             this.occupants.push(occupant)
                //         }

                //         // Set all
                //         const occupantMap = this.occupants.map((occupant) => occupant.$key)
                //         this.formData.controls['occupants'].setValue(occupantMap)
                //     })
                // })
            }

            // Analytics
            const parkingRef = doc(this.parkingService.firestore, parkingsPath(), this.parkingKey)
            const userId = this.authenticationService.uid
            this.trackingService.addEntry(TrackingEntryType.VIEW, parkingRef, userId)
        })
    }

    updateSelectedPlan(plan: Plan) {
        if (plan) {
            this.selectedPlan = plan
            this.formData.controls['planTerm'].setValue(plan.term)
        }

        // If there is a search cookie then use that otherwise select any

        // Set to default if not found?
        // if (this.parking.plans) {
        //     this.parking.plans.forEach((plan) => {
        //         this.selectedPlan = plan
        //     })
        // }
    }

    formChanges(changes: { baysBooked?: number; contractLength?: number; planTerm?: string; startDate?: string }) {
        let term = changes.planTerm ? changes.planTerm : this.formData.value.planTerm
        let bays = changes.baysBooked ? changes.baysBooked : this.formData.value.baysBooked
        let contractLength = changes.contractLength ? changes.contractLength : this.formData.value.contractLength
        let startDate = changes.startDate ? changes.startDate : this.formData.value.startDate

        if (this.parking.plans && changes.planTerm) {
            console.log(this.parking.plans)
            this.selectedPlan = this.parking.plans.find((i) => i.term === term) as Plan
            console.log(this.selectedPlan)
        }

        this.updateEndDate(term, startDate, contractLength)

        // this.totalDailySavings = this.parking.dailySavings * contractLength * 30.5

        this.occupantLimit = bays
    }

    flexDateSelectorChanged(flexSelectedDates: string) {
        // Options: halfMonth, thisMonth, nextMonth
        if (flexSelectedDates == 'halfMonth') {
            this.selectedPlan = this.parking.plans?.find((i) => i.term === Term.FLEX_HALF_MONTH) as Plan
            this.formData.controls['startDate'].setValue(this.middleOfThisMonth.format('yyyy-MM-DD'))
            this.formData.controls['endDate'].setValue(this.endOfThisMonth.format('yyyy-MM-DD'))
        }
        if (flexSelectedDates == 'thisMonth') {
            this.selectedPlan = this.parking.plans?.find((i) => i.term === Term.FLEX_MONTH) as Plan
            this.formData.controls['startDate'].setValue(this.startOfThisMonth.format('yyyy-MM-DD'))
            this.formData.controls['endDate'].setValue(this.endOfThisMonth.format('yyyy-MM-DD'))
        }
        if (flexSelectedDates == 'nextMonth') {
            this.selectedPlan = this.parking.plans?.find((i) => i.term === Term.FLEX_MONTH) as Plan
            this.formData.controls['startDate'].setValue(this.startOfNextMonth.format('yyyy-MM-DD'))
            this.formData.controls['endDate'].setValue(this.endOfNextMonth.format('yyyy-MM-DD'))
        }

        // startOfThisMonth: moment.Moment
        // middleOfThisMonth: moment.Moment
        // endOfThisMonth: moment.Moment
        // startOfNextMonth: moment.Moment
        // endOfNextMonth: moment.Moment

        this.formData.controls['flexDateSelector'].setValue(flexSelectedDates)
    }

    updateEndDate(term: string, startDate: Date, contractLength: any) {
        let lengthUnit = 'M'
        if (term == Term.MONTHLY) {
            lengthUnit = 'M'
        }
        if (term == Term.DAILY) {
            lengthUnit = 'd'
        }
        if (term == Term.ONCEOFF) {
            lengthUnit = 'd'
        }

        let newEndDate = moment(startDate).add(contractLength, lengthUnit).subtract(1, 'day')
        // if (term === Term.Daily) {
        //    newEndDate = newEndDate.subtract(1, "day")
        // }

        if (this.formData.controls['endDate']) {
            this.formData.controls['endDate'].setValue(newEndDate.format('yyyy-MM-DD'))
        }
    }

    async createSubscription() {
        this.formLoading = true

        if (this.parking.parkingType === ParkingType.PARKING_EVENTS) {
            if (!this.formData.value.occupants) {
                this.toastrService.error('No occupants selected.')
                return
            }

            if (this.formData.value.occupants.length > this.formData.value.baysBooked) {
                this.toastrService.error('You have too many occupants selected.')
                return
            }

            if (this.formData.value.occupants.length < this.formData.value.baysBooked) {
                this.toastrService.error('You have too few occupants selected.')
                return
            }
        }

        if (this.authenticationService.isAnonymous) {
            await this.router.navigate([`/sign-up`], {
                queryParams: { returnUrl: this.router.url },
            })
            return
        }

        if (!this.clientService.activeClient || !this.clientService.activeClient.firstName) {
            console.log('no client')
            await this.router.navigate([`/client/create`], { queryParams: { returnUrl: this.router.url } })
            return
        }

        await this.authenticationService.waitForUid()

        // client: this.clientService.activeClient!,
        // parking: this.parking,
        // plan: this.selectedPlan,
        // contractLength: this.formData.value.contractLength,
        // baysBooked: this.formData.value.baysBooked,
        // startDate: this.formData.value.startDate,
        // endDate: this.formData.value.endDate,
        // occupantKeys: this.formData.value.occupants,

        const client = this.clientService.activeClient

        let payGateClient = new PayGateClient({
            firstName: client.firstName,
            lastName: client.lastName,
            email: client.$email,
            mobile: client.$phoneNumber,
            key: client.$key,
        })

        const subscription = new Subscription()
        subscription.denormClientFirstName = client.firstName
        subscription.denormClientLastName = client.lastName
        subscription.denormParkingTitle = this.parking.title
        subscription.denormMerchantCompanyName = this.parking.denormMerchantCompanyName

        const now = serverTimestamp() as firestore.Timestamp

        subscription.createdAt = now
        subscription.updatedAt = now

        subscription.clientRef = this.clientService.getDocRef(client.$key!) as any
        subscription.parkingRef = this.parkingService.getDocRef(this.parking.$key!) as any

        subscription.plan = new Plan(this.selectedPlan)

        subscription.period = this.formData.value.contractLength
        subscription.baysBooked = this.formData.value.baysBooked

        const startDateObject = new Date(this.formData.value.startDate)
        subscription.startDate = firestore.Timestamp.fromDate(startDateObject)

        const endDateObject = new Date(this.formData.value.endDate)
        subscription.endDate = firestore.Timestamp.fromDate(endDateObject)

        subscription.merchantRef = this.parking.merchantRef
        subscription.status = ContractStatus.new

        subscription.payGateClient = payGateClient

        const occupantKeys = this.formData.value.occupants

        if (occupantKeys) {
            const occupantRefs: any[] = []
            occupantKeys.forEach((occupantKey: string) => {
                occupantRefs.push(doc(this.parkingService.firestore, occupantsPath(client.$key as string), occupantKey) as any)
            })

            subscription.occupantRefs = occupantRefs
        }

        const ipResponse = await GlobalService.getPublicIP()
        const userActionLog = new UserActionLog({
            firstName: client.firstName,
            lastName: client.lastName,
            email: client.$email,
            mobile: client.$phoneNumber,
            ip: ipResponse.ip,
            agent: navigator.userAgent,
            date: now,
        })

        subscription.contractCreationLog = userActionLog

        const response = await this.subscriptionService.create(subscription)
        console.log(response)
        // addDoc(collection(this.firestore, subscriptionsPath()), subscription.parse()).then((docRef) => {
        //     resolve({ status: 'success', subscriptionReference: docRef.id })
        // })

        // Track add to cart
        this.omnisendService.getSetCart({
            activeClient: this.clientService.activeClient!,
            subscriptionKey: response.subscriptionReference,
            parking: this.parking,
            quantity: this.formData.value.baysBooked * this.formData.value.contractLength,
            priceInCents: this.parking.defaultPriceInCents,
            orderTotal: this.formData.value.baysBooked * this.formData.value.contractLength * this.parking.defaultPriceInCents,
        })

        const feeInCents = this.selectedPlan.feeInCents * this.formData.value.baysBooked * this.formData.value.contractLength
        this.googleAnalyticsService.event(GaActionEnum.ADD_TO_CART, this.parking.parkingType, this.parking.title, feeInCents / 100)

        this.formLoading = false

        if (response.status === 'success') {
            this.openPopup = true
            this.subscriptionReference = response.subscriptionReference

            // Reset the popup
            setTimeout(() => {
                this.openPopup = false
            }, 100)
        } else {
            console.log('TODO')
        }
    }

    activeSlideChange(index: number) {
        this.currentImageIndex = index + 1
        if (index > this.highestIndex) {
            this.highestIndex = index
        }
    }

    warningDisabledDate() {
        this.toastrService.error('You cannot change the end date. Please change the contract duration.')
    }

    openModalOccupant() {
        if (!this.authenticationService.isAnonymous) {
            this.modalRefOccupant = this.modalService.show(this.popupOccupant, { backdrop: 'static' })
            return false
        } else {
            this.router.navigate([`/sign-up`], {
                queryParams: { returnUrl: this.router.url },
            })
            return
        }
    }

    createOccupant() {
        // if (this.formDataOccupant.value.fullName == '' && this.formDataOccupant.value.numberPlate == '') {
        //     this.toastrService.error('You must provide either a driver name or a number plate.')
        //     return
        // }

        this.formLoading = false

        const occupant = new Occupant({
            fullName: this.formDataOccupant.value.fullName,
            model: this.formDataOccupant.value.model,
            colour: this.formDataOccupant.value.colour,
            registration: this.formDataOccupant.value.numberPlate,
            description: this.formDataOccupant.value.description,
            createdAt: serverTimestamp() as firestore.Timestamp,
            updatedAt: serverTimestamp() as firestore.Timestamp,
        })

        this.authenticationService.waitForUid().then(() => {
            addDoc(collection(this.parkingService.firestore, occupantsPath(this.clientService.activeClient?.$key as string)), occupant.parse()).then((docRef) => {
                this.closeModalOccupant()
                console.log({ status: 'success', occupantsRef: docRef.id })

                // Select that option
                const currentOccupants = this.formData?.get('occupants')?.value || []
                currentOccupants.push(docRef.id)
                this.formData.get('occupants')?.setValue(currentOccupants)
            })
        })
    }

    closeModalOccupant() {
        // this.modalService.hide()
        this.modalRefOccupant?.hide()
        // this.onClosed.emit()
    }

    closeSaveFormModal() {
        this.modalRef?.hide()
    }

    openImageModal(template: TemplateRef<any>) {
        this.modalRef = this.modalService.show(template, {
            class: 'modal-lg',
            backdrop: 'static',
            keyboard: false, // This will also prevent closing with the keyboard 'Esc' button
        })
    }

    toggleMobileMapView() {
        this.showMobileMap = !this.showMobileMap
    }

    scrollToContact(event: Event): void {
        event.preventDefault()

        this.accordionContactState = AccordionState.EXPANDED

        const element = document.querySelector('#contact-parkupp')
        if (element) {
            const offset = 100 // Adjust this value based on your needs
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
            const offsetPosition = elementPosition - offset
            window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
        }
    }

    gotoPayFastSpecialLink() {
        const url = `${environment.host}/parking/${this.parking.$key}?allowPaymentType=${SubscriptionType.PAYFAST}`
        location.href = url
    }
}
